import React, { useEffect, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { MastheadSkeletonSSR } from '@/packages/ldp-core-sdm/src/skeletons/masthead-ssr/masthead-skeleton-ssr';

// Preload the chunk at module level so we can track when it's ready
const mastheadChunkPromise = import('./masthead-ssr');

// Dynamic import using the same promise "mastheadChunkPromise"
const MastheadSSRComponent = dynamic(() => mastheadChunkPromise, {
  ssr: false,
});

// Extract menu items from headerSection for SSR skeleton
export const extractMenuItems = (headerSection: any) => {
  try {
    const components = headerSection?.categoryNavigationSection?.components;
    if (!Array.isArray(components)) return undefined;

    return components.map((item: any) => ({
      label: item?.data?.callToAction?.text || '',
      url: item?.data?.callToAction?.url || '#',
    })).filter((item: any) => item.label);
  } catch {
    return undefined;
  }
};

// Extract logo data from headerSection for SSR skeleton
export const extractLogoData = (headerSection: any) => {
  try {
    const logoSection = headerSection?.logoSection?.components?.[0]?.data;
    if (!logoSection) return undefined;

    return {
      largeLogo: {
        url: logoSection?.largeLogo?.url,
        altText: logoSection?.largeLogo?.altText,
      },
      callToAction: {
        url: logoSection?.callToAction?.url,
      },
    };
  } catch {
    return undefined;
  }
};

interface MastheadSSRWrapperProps {
  headerSection: any;
}

// Wrapper component to show masthead skeleton until the real component is ready
export const MastheadSSRWrapper = ({ headerSection }: MastheadSSRWrapperProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isChunkLoaded, setIsChunkLoaded] = useState(false);

  const menuItems = useMemo(() => extractMenuItems(headerSection), [headerSection]);
  const logo = useMemo(() => extractLogoData(headerSection), [headerSection]);

  useEffect(() => {
    setIsMounted(true);
    // Track when the chunk finishes loading
    mastheadChunkPromise.then(() => setIsChunkLoaded(true));
  }, []);

  // Show skeleton until both mounted AND chunk is loaded
  if (!isMounted || !isChunkLoaded) {
    return <MastheadSkeletonSSR logo={logo} menuItems={menuItems} />;
  }

  return <MastheadSSRComponent headerSection={headerSection} />;
};

export default MastheadSSRWrapper;
